/* eslint-disable @typescript-eslint/no-explicit-any */
import { AUTH_TOKEN_COOKIE_NAME } from 'src/utils/env';
import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
} from '@apollo/client';
import { ErrorLink } from '@apollo/client/link/error';
import { GetServerSidePropsContext } from 'next';
import { RetryLink } from '@apollo/client/link/retry';
import { authToken } from 'src/app/auth/cookies';
import { useMemo } from 'react';
import Const from 'src/config/Const';

// API Client (api.beek.io)
let apolloClientApi: ApolloClient<NormalizedCacheObject> | undefined;
// Core Client (core.beek.io)
let apolloClientCore: ApolloClient<NormalizedCacheObject> | undefined;

export enum BeekGraphqlSchema {
  api = 'api',
  core = 'core',
}

const errorLink = new ErrorLink(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.map(
      ({ message, locations, path }) =>
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
    );
  }
  if (networkError) {
    // TODO: https://beek.atlassian.net/browse/ENG-318
  }
});

const getAuthToken = (
  context?: GetServerSidePropsContext
): string | undefined => {
  if (context) {
    return context.req.cookies[AUTH_TOKEN_COOKIE_NAME];
  }
  return authToken();
};

const createAuthLink = (context?: GetServerSidePropsContext): ApolloLink =>
  new ApolloLink((operation, forward) => {
    operation.setContext({
      headers: {
        authorization: getAuthToken(context) || null,
      },
    });
    return forward(operation);
  });

const retryLink = new RetryLink({
  delay: {
    initial: 1000,
    max: 10000,
    jitter: true,
  },
  attempts: {
    max: 6,
    retryIf: (error): boolean | Promise<boolean> => !!error,
  },
});

/**
 * This is the primary `beek` API service (api.beek.io) you should use 95% of the time.
 * It used to be called `beek_core`, but it shouldn't be confused with `createCoreApolloClient()` which
   uses `core` instead.

   @see createCoreApolloClient()
 */
const createApiApolloClient = (
  context?: GetServerSidePropsContext
): ApolloClient<any> =>
  new ApolloClient({
    ssrMode: typeof window === 'undefined',
    link: ApolloLink.from([
      errorLink,
      createAuthLink(context),
      retryLink,
      new HttpLink({ uri: Const.graphqlBeekApiWebUrl }),
    ]),
    cache: new InMemoryCache(),
  });

/**
 * This initializes Apollo using `core` instead of `api`, and was created c. 2019 for poor but well-intentioned reasons.
 * As much as we'd like to remove it, it's still being used until we can properly merge them.
 * Use `createApiApolloClient()` for new queries instead of this (unless you have a good reason to use `core.beek.io`).

 * @deprecated
 * @see createApiApolloClient()
 */
const createCoreApolloClient = (
  _context?: GetServerSidePropsContext // eslint-disable-line @typescript-eslint/no-unused-vars
): ApolloClient<any> =>
  new ApolloClient({
    ssrMode: typeof window === 'undefined',
    link: new HttpLink({
      uri: Const.graphqlUrl,
      credentials: 'same-origin',
    }),
    cache: new InMemoryCache(),
  });

/**
 * Initializes or creates an Apollo singleton for the provided schema type.
 * Most of the time, you will want to initialize with **BeekGraphqlSchema.api**
   if you're creating a new query.

 * Calling this function with `core` should only be done for compatibility reasons
   until all the APIs can be merged into `api`.
 */
export const initializeApollo = (
  schemaType: BeekGraphqlSchema = BeekGraphqlSchema.core,
  context?: GetServerSidePropsContext
): ApolloClient<any> => {
  // For SSG and SSR always create a new Apollo Client
  if (context) {
    return schemaType === BeekGraphqlSchema.core
      ? createCoreApolloClient(context)
      : createApiApolloClient(context);
  }

  // For regular requests, use the cached client for the requested schema or
  // initialize a new one, cache it, and return it.
  if (schemaType === BeekGraphqlSchema.core) {
    if (!apolloClientCore) {
      apolloClientCore = createCoreApolloClient();
    }
    return apolloClientCore;
  }
  if (!apolloClientApi) {
    apolloClientApi = createApiApolloClient();
  }
  return apolloClientApi;
};

export const useApollo = (): ApolloClient<any> => {
  const store = useMemo(() => initializeApollo(BeekGraphqlSchema.api), []);
  return store;
};
